<template>
  <master-layout
    id="calendar-top"
    :enable-scroll-event="true"
    @on-scroll="onScroll"
    :show-footer="false"
    :full-width="true"
  >
    <StickyHeader
      v-model="isHeaderHidden"
      :peek-height="0"
      scrollElementId="calendar-top"
      :enable-peek="!isMonthView"
      prevent-bounce-reveal
    >
      <template #left>
        <BackButton compact :customBack="() => ionRouter.back()" />
      </template>
      <template #right>
        <div v-if="isLeaderEvents" class="px-[8px] py-[4px] rounded-[16px] bg-[#191817]">
          <p class="text-[0.75rem] md:text-[0.875rem] text-white font-semibold">Leaders</p>
        </div>
      </template>
      <template #bottom>
        <div class="flex items-center justify-between px-5 md:px-6 lg:px-8 pt-[4px] pb-[8px]">
          <div
            class="bg-[#E8DCC7] pl-[12px] pr-[10px] py-[8px] rounded-[8px] flex cursor-pointer"
            @click="isSelectorOpen = true"
          >
            <h4
              class="text-[#3C3A36] text-[16pt] md:text-[18pt] tracking-[-0.03em] leading-[1.25rem] md:leading-[1.5rem] font-inter font-black uppercase mr-[8px]"
            >
              {{ topMonth }}
            </h4>
            <img class="w-[16px] h-[16px] mt-[1px]" src="/assets/icons/chevron-down.svg" alt="" />
          </div>
          <div class="flex">
            <div
              v-if="!isMonthView"
              class="border-[1px] border-solid border-[#3C3A36] rounded-[10px] w-[36px] h-[36px] md:w-[44px] md:h-[44px] p-[8px] md:p-[10px] mr-[8px] cursor-pointer"
              @click="toMonthView"
            >
              <img
                class="w-[20px] h-[20px] md:w-[24px] md:h-[24px]"
                src="/assets/icons/calendar/calendar.svg"
                alt="month view icon"
              />
            </div>
            <div
              v-else
              class="border-[1px] border-solid border-[#3C3A36] rounded-[10px] w-[36px] h-[36px] md:w-[44px] md:h-[44px] p-[8px] md:p-[10px] mr-[8px] cursor-pointer"
              @click="toListView"
            >
              <img
                class="w-[20px] h-[20px] md:w-[24px] md:h-[24px]"
                src="/assets/icons/calendar/list.svg"
                alt="list view icon"
              />
            </div>
            <div
              class="border-[1px] border-solid border-[#3C3A36] rounded-[10px] w-[36px] h-[36px] md:w-[44px] md:h-[44px] p-[8px] md:p-[10px]"
              @click="isFilterOpen = true"
            >
              <img
                class="w-[20px] h-[20px] md:w-[24px] md:h-[24px]"
                src="/assets/icons/calendar/filter-icon.svg"
                alt="filter-icon"
              />
              <p
                v-if="selectedEvents.length > 0"
                class="relative top-[-32px] left-[18px] rounded-full bg-[#EC4E20] w-min min-w-[18px] h-[18px] text-[0.625rem] text-center leading-[0.875rem] py-[2px] px-[4px] font-bold text-[#FFFFFF]"
              >
                {{ selectedEvents.length }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </StickyHeader>
    <div class="flex-1 min-h-full bg-[#F6F4F0] pb-[64px] overflow-x-hidden">
      <!-- Loading skeleton for list view -->
      <SkeletonCalendarList
        v-if="isLoading && !isMonthView"
        class="bg-[#F6F4F0] transition-padding duration-300 ease-out"
        :style="
          isIos
            ? {
                paddingTop: 'env(safe-area-inset-top)',
              }
            : {}
        "
      />
      <!-- Error state -->
      <div
        v-else-if="fetchFailed"
        class="w-full min-h-screen flex flex-col items-center justify-center px-5 bg-[#F6F4F0]"
      >
        <p class="text-[1rem] text-center text-[#3C3A36] font-semibold mb-[8px]">
          Calendar is temporarily unavailable.
        </p>
        <p class="text-[0.875rem] text-center text-[#3C3A36] mb-[24px]">Please try again later.</p>
        <button
          class="px-[24px] py-[12px] bg-[#3C3A36] text-white text-[0.875rem] font-semibold rounded-[10px]"
          @click="
            () => {
              fetchFailed = false;
              ionViewWillEnter();
            }
          "
        >
          Retry
        </button>
      </div>
      <CalendarList
        v-if="!isMonthView && !isLoading && !fetchFailed"
        class="bg-[#F6F4F0] transition-padding duration-300 ease-out"
        :class="{
          'pt-[50px]': !isHeaderHidden,
          'pt-[0px]': isHeaderHidden,
        }"
        :style="
          isIos
            ? {
                paddingTop: isHeaderHidden
                  ? 'env(safe-area-inset-top)'
                  : 'calc(50px + env(safe-area-inset-top))',
              }
            : {}
        "
        :monthEvents="monthEvents"
        :trainingAnnouncements="trainingAnnouncements"
        ref="calendarList"
      />
      <CalendarMonthList
        v-else-if="isMonthView && !fetchFailed"
        class="transition-padding duration-300 ease-out"
        :class="{
          'pt-[130px]': !isHeaderHidden,
          'pt-[0px]': isHeaderHidden,
        }"
        :style="
          isIos
            ? {
                paddingTop: isHeaderHidden
                  ? 'env(safe-area-inset-top)'
                  : 'calc(130px + env(safe-area-inset-top))',
              }
            : {}
        "
        :months="months"
        :monthEvents="monthEvents"
        :eventData="eventData"
        scrollElementId="calendar-top"
        @hideFooter="showOnEvent = !val"
        ref="calendarMonthList"
      />
    </div>
    <Modal v-model="isFilterOpen">
      <template #header> Filter View </template>
      <CheckboxGroup v-model="selectedEvents" :options="tagOptions" />
    </Modal>
    <Modal v-model="isSelectorOpen">
      <template #header> Select Month </template>
      <YearRadioGroup v-model="selectedMonth" :options="monthOptions" />
    </Modal>
  </master-layout>
</template>

<script>
import CalendarList from '@/components/Calendar/Events/CalendarList.vue';
import CalendarMonthList from '@/components/Calendar/Events/CalendarMonthList.vue';
import SkeletonCalendarList from '@/components/Calendar/Events/SkeletonCalendarList.vue';
import StickyHeader from '@/components/common/StickyHeader.vue';
import CheckboxGroup from '@/components/common/CheckboxGroup.vue';
import YearRadioGroup from '@/components/common/YearRadioGroup.vue';
import Modal from '@/components/common/Modal.vue';
import BackButton from '@/components/common/BackButton.vue';
import dayjs from 'dayjs';
import { mapState } from 'vuex';
import { isIosNativeOnly } from '@/utils/platform-check';
import { useIonRouter } from '@ionic/vue';

export default {
  setup() {
    const ionRouter = useIonRouter();
    return { ionRouter };
  },
  components: {
    CalendarList,
    CalendarMonthList,
    SkeletonCalendarList,
    StickyHeader,
    CheckboxGroup,
    YearRadioGroup,
    Modal,
    BackButton,
  },
  data() {
    return {
      topMonth: '',
      selectedEvents: [],
      selectedMonth: '',
      scrollValue: 0,
      isFilterOpen: false,
      isSelectorOpen: false,
      viewMode: 'list',
      showOnEvent: true,
      suppressSelectedMonthWatcher: false,
      isHeaderHidden: false,
      fetchFailed: false,
    };
  },
  computed: {
    ...mapState('calendar', {
      filters: state => state.filters,
      showFilterBar: state => state.showFilterBar,
      isLeadersCalendar: state => state.isLeadersCalendar,
      trackedMonthState: state => state.currentMonth,
    }),
    ...mapState('cms', [
      'calendarEvents',
      'calendarNotifications',
      'calendarTrainingAnnouncements',
    ]),
    ...mapState('cms', {
      isCmsLoading: state => state.loading,
    }),
    ...mapState('profile', {
      isLeader: state => state.isLeader,
    }),
    trackedMonth: {
      get() {
        return this.trackedMonthState;
      },
      set(value) {
        if (value && value !== this.trackedMonthState) {
          this.$store.dispatch('calendar/updateCurrentMonth', value);
        }
      },
    },
    isLeaderEvents() {
      return this.eventData.some(event => event.isLeadersOnly) && this.isLeader;
    },
    isLoading() {
      if (this.fetchFailed) return false;
      return this.isCmsLoading || Object.keys(this.calendarEvents).length === 0;
    },
    eventData() {
      return Object.values(this.calendarEvents).flat() || [];
    },
    months() {
      if (this.isCmsLoading || Object.keys(this.calendarEvents).length === 0) {
        return [];
      }
      if (this.eventData.length === 0) {
        return [{ month: dayjs().format('MMMM'), year: dayjs().format('YYYY') }];
      }

      const dates = this.eventData.map(event => new Date(event.startDate));
      const startDate = dayjs(new Date(Math.min(...dates)));
      const endDate = dayjs(
        new Date(Math.max(...this.eventData.map(event => new Date(event.endDate)))),
      );

      const monthsArray = [];
      const totalMonths = endDate.diff(startDate, 'month');

      for (let i = 0; i <= totalMonths; i++) {
        const current = startDate.add(i, 'month');
        monthsArray.push({
          month: current.format('MMMM'),
          year: current.format('YYYY'),
        });
      }

      return monthsArray;
    },
    tags() {
      const uniqueTags = this.getUniqueTags(this.eventData);
      return this.isLeaderEvents ? [{ id: 'A', name: 'Leaders' }, ...uniqueTags] : uniqueTags;
    },
    tagOptions() {
      return this.tags.map(tag => ({ label: tag.name, value: tag.id }));
    },
    monthEvents() {
      const filteredEvents = this.getFilteredEvents();
      return this.getMonthEvents(filteredEvents);
    },
    monthOptions() {
      return this.monthEvents
        .filter(month => month.event?.length > 0)
        .map(month => ({ label: month.month, value: month.name, year: month.year }));
    },
    isMonthView() {
      return this.viewMode === 'month';
    },
    isIos() {
      return isIosNativeOnly();
    },
    trainingAnnouncements() {
      return this.calendarTrainingAnnouncements || [];
    },
  },
  async ionViewWillEnter() {
    this.fetchFailed = false;

    const cmsEntities = [
      { entityName: 'calendar/events', stateName: 'calendarEvents' },
      { entityName: 'calendar/filters', stateName: 'calendarEventFilters' },
      { entityName: 'calendar/last-updated-date', stateName: 'calendarLastUpdatedDate' },
      { entityName: 'calendar/download-link', stateName: 'calendarDownloadLink' },
      { entityName: 'calendar/notifications', stateName: 'calendarNotifications' },
      { entityName: 'calendar/training-announcements', stateName: 'calendarTrainingAnnouncements' },
    ];

    const fetchPromises = cmsEntities.map(entity =>
      this.$store.dispatch('cms/fetchCmsContent', {
        ...entity,
        options: { refresh: true },
      }),
    );

    try {
      await Promise.all([...fetchPromises, this.$store.dispatch('updates/getUpdates', 'all')]);
    } catch (error) {
      if (error?.response?.status === 401) throw error;
      this.fetchFailed = true;
      return;
    }

    this.viewMode = 'list';
    this.showOnEvent = true;
    this.initializeMonthSelection();
  },
  mounted() {
    document.getElementById('calendar-top')?.scrollToBottom?.();
  },
  watch: {
    selectedMonth(newVal) {
      if (!newVal || this.suppressSelectedMonthWatcher) return;
      this.trackedMonth = newVal;
      this.syncTopToTrackedMonth({ behavior: 'smooth' });
      this.isSelectorOpen = false;
    },
    selectedEvents() {
      this.syncTopToTrackedMonth({ behavior: 'auto' });
    },
    viewMode() {
      this.selectedMonth = this.trackedMonth || this.selectedMonth;
      this.syncTopToTrackedMonth({ behavior: 'auto' });
    },
    eventData: {
      handler(newData) {
        if (newData.length > 0 && !this.topMonth) {
          const defaultLabel = this.getDefaultMonthLabel();
          const parsed = this.parseMonthLabel(defaultLabel);
          if (parsed) {
            this.topMonth = parsed.month;
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    initializeMonthSelection() {
      const initialMonth = this.trackedMonthState || this.getDefaultMonthLabel();
      if (!initialMonth) return;

      this.trackedMonth = initialMonth;
      this.suppressSelectedMonthWatcher = true;
      this.selectedMonth = initialMonth;
      this.$nextTick(() => {
        this.suppressSelectedMonthWatcher = false;
        this.syncTopToTrackedMonth({ behavior: 'auto' });
      });
    },
    getFilteredEvents() {
      if (!this.selectedEvents.length) {
        return this.eventData;
      }

      const tagFiltered = this.eventData.filter(event =>
        event.tags.some(tag => this.selectedEvents.includes(tag.id)),
      );

      if (!this.selectedEvents.includes('A')) {
        return tagFiltered;
      }

      const leaderEvents = this.eventData.filter(event => event.isLeadersOnly);
      return [...new Set([...leaderEvents, ...tagFiltered])];
    },
    getMonthEvents(eventData) {
      return this.months.map(month => {
        const monthKey = `${month.month} ${month.year}`;
        const filteredEvents = eventData.filter(
          e =>
            (dayjs(e.startDate).format('MMMM YYYY') === monthKey ||
              dayjs(e.endDate).format('MMMM YYYY') === monthKey) &&
            (this.isLeader || !e.isLeadersOnly),
        );
        return {
          name: monthKey,
          month: month.month,
          year: month.year,
          event: filteredEvents,
        };
      });
    },
    getUniqueTags(events) {
      const tags = events.flatMap(event => event.tags).filter(Boolean);
      const uniqueTags = new Map(tags.map(tag => [tag.id, tag]));
      return Array.from(uniqueTags.values());
    },
    onScroll(ev) {
      this.scrollValue = ev.detail.scrollTop;
      const ref = this.isMonthView ? this.$refs.calendarMonthList : this.$refs.calendarList;
      const top = ref?.findTopVisibleComponent ? ref.findTopVisibleComponent() : null;
      this.updateTopMonth(top);
    },
    syncTopToTrackedMonth({ behavior = 'auto' } = {}) {
      const label = this.trackedMonth || this.getDefaultMonthLabel();
      if (!label) return;

      this.scrollToMonth(label, { behavior });
      this.$nextTick(() => {
        const ref = this.isMonthView ? this.$refs.calendarMonthList : this.$refs.calendarList;
        const top = ref?.findTopVisibleComponent?.() || this.parseMonthLabel(label);
        this.updateTopMonth(top);
      });
    },
    scrollToMonth(label, { behavior = 'auto' } = {}) {
      const parsed = this.parseMonthLabel(label);
      if (!parsed?.month || !parsed?.year) return;

      this.$nextTick(() => {
        const ref = this.isMonthView ? this.$refs.calendarMonthList : this.$refs.calendarList;
        const container = ref?.$el;
        if (!container) return;

        const selector = `.eventItem[month="${parsed.month}"][year="${parsed.year}"]`;
        let target = container.querySelector(selector);
        if (!target) {
          target = container.querySelector('.eventItem');
          if (!target) return;
        }

        const block = this.isMonthView ? 'center' : 'start';
        target.scrollIntoView({ behavior, block });

        if (!this.isMonthView) {
          setTimeout(
            () => {
              const layout = document.getElementById('calendar-top');
              if (!layout?.scrollByPoint) return;
              const rect = target.getBoundingClientRect();
              const offset = rect.top - 110;
              if (Math.abs(offset) > 2) {
                layout.scrollByPoint(0, offset, 300);
              }
            },
            behavior === 'smooth' ? 400 : 0,
          );
        }
      });
    },
    parseMonthLabel(label) {
      if (!label) return null;
      const tokens = label.trim().split(' ');
      if (tokens.length < 2) return null;
      return { month: tokens.slice(0, -1).join(' '), year: tokens[tokens.length - 1] };
    },
    updateTopMonth(result) {
      if (!result?.month) return;
      this.topMonth = result.month;

      if (result.year) {
        const label = `${result.month} ${result.year}`;
        if (label !== this.trackedMonthState) {
          this.trackedMonth = label;
        }
        if (this.selectedMonth !== label) {
          this.suppressSelectedMonthWatcher = true;
          this.selectedMonth = label;
          this.$nextTick(() => {
            this.suppressSelectedMonthWatcher = false;
          });
        }
      }
    },
    getDefaultMonthLabel() {
      if (!this.eventData.length) {
        return dayjs().format('MMMM YYYY');
      }

      const now = dayjs();
      const sorted = [...this.eventData].sort((a, b) =>
        dayjs(a.startDate).diff(dayjs(b.startDate)),
      );

      let target = sorted.find(event => {
        const endDate = event.endDate || event.startDate;
        return dayjs(endDate).endOf('day').diff(now) >= 0;
      });

      if (!target) {
        target = sorted[sorted.length - 1];
      }

      return target ? dayjs(target.startDate).format('MMMM YYYY') : null;
    },
    toMonthView() {
      this.viewMode = 'month';
      this.showOnEvent = true;
      this.isSelectorOpen = false;
    },
    toListView() {
      this.viewMode = 'list';
      this.showOnEvent = true;
    },
  },
};
</script>

<style scoped>
.safe-area {
  padding-top: env(safe-area-inset-top);
}

.transition-padding {
  transition: padding-top 0.3s ease-out;
}
</style>
